import React, { useEffect, useMemo } from 'react';
import {
  Box,
  List,
  Option,
  OptionKey,
  PaginationBar,
  SearchInput,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { useFilters } from '@/utils';

import { PER_PAGE, REPORT_TYPE_OPTIONS, ScheduledReportStatus } from '../ScheduledReport.types';

import { useGetScheduledReportsQuery } from './queries';
import ScheduledReportItem from './ScheduledReportItem';

interface FilterValues {
  searchString: string;
  reportTypes: OptionKey[];
  status: OptionKey[];
  startDate: Date | null;
  endDate: Date | null;
  page: number;
  perPage: number;
}
const ScheduledReportsTab = () => {
  const { filtersValues, setFilterValue } = useFilters<FilterValues>(
    {
      searchString: '',
      reportTypes: [],
      status: [],
      startDate: null,
      endDate: null,
      page: 1,
      perPage: PER_PAGE,
    },
    {
      autoClear: true,
    }
  );
  useEffect(() => {
    setFilterValue.page(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isLoading, total, data } = useGetScheduledReportsQuery({
    searchValue: filtersValues.searchString,
    types: filtersValues.reportTypes,
    statuses: filtersValues.status,
    page: filtersValues.page,
    perPage: filtersValues.perPage,
  });
  const statusOptions = useMemo(() => {
    const options: Option[] = [
      {
        key: ScheduledReportStatus.Active,
        title: 'Active',
      },
      {
        key: ScheduledReportStatus.Deactivated,
        title: 'Deactivated',
      },
    ];
    return options;
  }, []);
  return (
    <Box background="module" round="moduleRound" pad="spacing24" direction="column">
      <Box margin={{ bottom: 'l' }} direction="row" align="center" justify="between">
        <Box>
          <Text weight="bold" color="textBody" size="large" style={{ whiteSpace: 'nowrap' }}>
            Schedules: {total}
          </Text>
        </Box>
        <Box direction="row" justify="end">
          <Box width="180px">
            <SearchInput
              testId="statements-list_search"
              value={filtersValues.searchString}
              onChange={setFilterValue.searchString}
              placeholder="Search"
              hasSearchingHistory
            />
          </Box>
          <Box margin={{ left: 'spacing12' }} width="160px">
            <SelectDropdown
              id="type"
              allMode
              name="type"
              prefix="Type"
              dropdownStyles={{
                minWidth: '250px',
              }}
              values={filtersValues.reportTypes}
              onChangeValues={setFilterValue.reportTypes}
              options={REPORT_TYPE_OPTIONS}
              activeTitle
            />
          </Box>
          <Box margin={{ left: 'spacing12' }} width="160px">
            <SelectDropdown
              allMode
              id="status"
              name="status"
              prefix="Status"
              values={filtersValues.status}
              onChangeValues={setFilterValue.status}
              options={statusOptions}
              activeTitle
            />
          </Box>
        </Box>
      </Box>
      <List>
        <List.Header>
          <Box width="12px" />
          <List.Header.Item flex={4}>
            <Box pad={{ left: 'spacing24' }}>Name</Box>
          </List.Header.Item>
          <List.Header.Item flex={3}>Report type</List.Header.Item>
          <List.Header.Item flex={2}>Frequency</List.Header.Item>
          <List.Header.Item flex={2}>Starting date</List.Header.Item>
          <List.Header.Item flex={3}>Next scheduled date</List.Header.Item>
          <List.Header.Item flex={2}>Status</List.Header.Item>
          <Box width="24px" />
        </List.Header>
      </List>
      <Box direction="column">
        {isLoading ? (
          <List.Preloader count={3} />
        ) : (
          data.map((item) => <ScheduledReportItem key={item.id} {...item} />)
        )}

        {!isLoading && !total && <List.Placeholder />}
      </Box>
      {total > PER_PAGE && (
        <PaginationBar
          page={filtersValues.page}
          total={total}
          pageSize={filtersValues.perPage}
          onChangePage={setFilterValue.page}
          onChangePageSize={setFilterValue.perPage}
        />
      )}
    </Box>
  );
};

export default ScheduledReportsTab;
